import React, { useEffect, useState } from "react";
import { z } from "zod";
import { api, extractApiError } from "@/lib/api";
import { useT, useLocalized } from "@/lib/LanguageContext";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, Save, X, Loader2, Image as ImageIcon, Sparkles } from "lucide-react";
// RELATIVE paths on purpose (not "@/…"): these modules must be REALLY resolved under
// Jest, not mocked — the "@/" alias is not mapped on the Jest side (cf.
// lib/LanguageContext.jsx). A relative path resolves natively.
import TaxCategoryField from "./TaxCategoryField";
import LanguageAwareField from "./LanguageAwareField";
import useTaxProfile from "../hooks/useTaxProfile";
import useFieldErrors from "../hooks/useFieldErrors";
import { withAnyLangRule } from "../lib/validation";
import FieldError, { invalidInputClass } from "../components/ui/FieldError";
import { cn } from "../lib/utils";
import { fmtMoney } from "./money";
import useTenantCurrency from "../lib/useTenantCurrency";
import CatalogueCurrencyField, { isLegacyCurrency } from "./CatalogueCurrencyField";

// Mirrors server.py's write boundary (ProgramIn): CATALOGUE_NAME_MAX = 200,
// CATALOGUE_PRICE_MAX_CENTS = 10_000_000.
const NAME_MAX = 200;
const PRICE_MAX_CENTS = 10_000_000;

/**
 * BUG-003 — "New program → Save does nothing", and its RE-OPEN.
 *
 * ORIGINAL: `save()` was `try { await api.post(…) } finally { … }` with NO catch, so a
 * refused payload was an unhandled rejection — the spinner stopped and nothing said the
 * programme had not been created. Judging the form here means the call is never made and
 * the message lands on the offending field.
 *
 * RE-OPEN — "practitioners cannot create a Program or Product using only their working
 * language because the backend requires the French name". That was true and is now fixed
 * on BOTH sides: `ProgramIn.name_fr` is no longer `min_length=1`, and the rule is the
 * pair rule — AT LEAST ONE language, neither one specifically
 * (`server._require_a_name_in_some_language`). `withAnyLangRule` is that same rule
 * client-side, so a form can no longer accept what the server refuses, nor refuse what it
 * would accept.
 *
 * WHY ONE LANGUAGE IS ENOUGH: every reader falls back. `pickLocalized` walks
 * `_fr → _en` for a French visitor and `_en → _fr` for an English one, and on the server
 * `platform_helpers.catalogue_name` is the single resolver the checkout names the Stripe
 * line item with. The same asymmetry TestimonialsAdmin and BlogAdmin already rely on.
 * What stays refused is a programme with NO name at all — the original BUG-003 — because
 * that is a blank card in the shop and a line no checkout can describe.
 *
 * The price rule is a superRefine because `_price_dollars` is free text: parsing is part
 * of the rule ("" and "abc" are not 0, they are unstated), and a programme sold with a Buy
 * button must be chargeable — mirrored here client-side, though the API's own
 * `price_cents: int = Field(..., gt=0, ...)` (server.py ProgramIn) already refuses 0 too.
 */
// `.optional()` on every field here is not laxity: a MISSING key would abort the object
// parse, and an aborted parse reports zod's own `invalid_type` on a field no one renders —
// a save refused with nothing highlighted, the very defect being fixed here. An absent
// price is judged by the rule below (NaN → "enter a price"), on the visible field; an
// absent language is exactly what withAnyLangRule judges (it treats a non-string as blank).
const programSchema = withAnyLangRule(z.object({
  name_fr: z.string().trim().max(NAME_MAX, "validation.tooLong").optional(),
  name_en: z.string().trim().max(NAME_MAX, "validation.tooLong").optional(),
  _price_dollars: z.union([z.string(), z.number()]).optional(),
}), "name_fr", "name_en").superRefine((form, ctx) => {
  const cents = Math.round(Number(form._price_dollars) * 100);
  const message = !Number.isFinite(cents) || cents <= 0
    ? "catalogue.validation.pricePositive"
    : cents > PRICE_MAX_CENTS ? "catalogue.validation.priceMax" : null;
  if (message) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["_price_dollars"], message });
});

const empty = {
  name_fr: "", name_en: "", tagline_fr: "", tagline_en: "",
  // NO `currency` — see ProductsAdmin's `empty`. The server denominates a new row in the
  // platform's settlement currency and keeps an existing row's on an edit.
  description_fr: "", description_en: "", price_cents: 0, _price_dollars: "0.00",
  duration_label: "", image_url: "", features_fr: [], features_en: [],
  featured: false, active: true, order: 0,
  tax_category: "standard", exemption_citation: "", sac_code: "",
};

// A FRESH blank form every time, never `empty` itself: `empty` is a module-level
// constant, so handing its own `features_*` arrays to `form` would alias the template to
// every editor session — one stray in-place update away from a permanently dirty "new"
// programme. `empty` declares the shape; this hands out instances of it.
const emptyForm = () => ({ ...empty, features_fr: [], features_en: [] });

export default function ProgramsAdmin() {
  const tenantCurrency = useTenantCurrency();
  const t = useT("crud");
  const ta = useT("admin");
  const tc = useT("common");
  const loc = useLocalized();
  const taxProfile = useTaxProfile();
  const { fieldErrors, validate, clearFieldError, clearAllErrors } = useFieldErrors();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [featInput, setFeatInput] = useState({ fr: "", en: "" });

  // An error states what the value was AT SUBMIT TIME; the next keystroke in that field
  // retires it. Only the validated fields go through this helper.
  const updateField = (k, v) => { setForm((f) => ({ ...f, [k]: v })); clearFieldError(k); };
  // One input, two languages, ONE message slot. The pair rule ("at least one language")
  // is reported on `name_fr` by withAnyLangRule, while the per-language ceiling is
  // reported on whichever language broke it — both have to reach the single field the
  // practitioner can see, or a save could be refused with nothing highlighted. A
  // keystroke in EITHER language retires both, because either one can be what fixes the
  // pair. Same shape as TestimonialsAdmin's `quoteError` / `updateQuote`.
  const nameError = fieldErrors.name_fr || fieldErrors.name_en;
  const updateName = (lang, v) => {
    setForm((f) => ({ ...f, [`name_${lang}`]: v }));
    clearFieldError("name_fr");
    clearFieldError("name_en");
  };
  const priceCeiling = (PRICE_MAX_CENTS / 100).toFixed(2);

  const load = async () => {
    const { data } = await api.get("/admin/programs");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  /**
   * BUG-219 — the ONE way to open the editor, for a new row (`p === null`) or an existing
   * one. The editor's state is not just `form`: `featInput` holds the "Includes" text the
   * practitioner has typed but not yet pushed with "+", and it lives outside `form`
   * precisely because it must never reach the payload. Opening used to be two separate
   * handlers, and the "New program" one reseeded `form` alone — so the previous
   * programme's un-added "Includes" draft stayed in the box, and the practitioner's very
   * next "+" committed it onto the NEW row, which the public programme card then shows.
   * A subset reset is only possible while there is more than one door; this is the door.
   */
  const openEditor = (p) => {
    setForm(p
      ? {
        ...empty, ...p,
        features_fr: p.features_fr || [], features_en: p.features_en || [],
        _price_dollars: ((p.price_cents || 0) / 100).toFixed(2),
      }
      : emptyForm());
    setFeatInput({ fr: "", en: "" });
    clearAllErrors();
    setEditing(p ?? "new");
  };

  const save = async () => {
    if (validate(programSchema, form)) { toast.error(tc("validation.fixHighlighted")); return; }
    setBusy(true);
    // `price_cents` is NEVER bound straight to an input: the practitioner types DOLLARS
    // (`_price_dollars`), converted to cents here — otherwise "497" typed at the
    // keyboard was stored as 4.97 $ (a real bug, cf. admin/money.js `toCents`).
    const { _price_dollars, ...rest } = form;
    const payload = {
      ...rest,
      price_cents: Math.round(Number(_price_dollars || 0) * 100),
      order: parseInt(form.order || 0, 10),
    };
    try {
      if (editing === "new") await api.post("/admin/programs", payload);
      else await api.put(`/admin/programs/${editing.id}`, payload);
    } catch (e) {
      toast.error(extractApiError(e, tc("errors.saveFailed"), tc));
      setBusy(false);
      return;
    }
    // The write above already succeeded — closing the editor here, not after the reload
    // below, is what keeps a failed reload from reading as a failed save: leaving the
    // editor open on that failure is what invited a retry that duplicated the row.
    setEditing(null);
    try {
      await load();
    } catch (e) {
      toast.error(extractApiError(e, ta("toast.reloadFailed"), tc));
    } finally {
      setBusy(false);
    }
  };

  const uploadImage = async (file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setForm((f) => ({ ...f, image_url: fullUrl }));
    } catch (e) {
      toast.error(extractApiError(e, ta("media.toast.uploadFailed"), tc));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(ta("programs.confirmDelete"))) return;
    try {
      await api.delete(`/admin/programs/${p.id}`);
    } catch (e) {
      toast.error(extractApiError(e, ta("toast.deleteFailed"), tc));
      return;
    }
    // The delete above already succeeded — a reload failure here is a stale LIST, not a
    // failed delete, and must not be reported as one.
    try {
      await load();
    } catch (e) {
      toast.error(extractApiError(e, ta("toast.reloadFailed"), tc));
    }
  };

  const addFeature = (lang) => {
    if (!featInput[lang]) return;
    setForm((f) => ({ ...f, [`features_${lang}`]: [...(f[`features_${lang}`] || []), featInput[lang]] }));
    setFeatInput({ ...featInput, [lang]: "" });
  };
  const removeFeature = (lang, idx) => {
    setForm((f) => ({ ...f, [`features_${lang}`]: f[`features_${lang}`].filter((_, i) => i !== idx) }));
  };

  return (
    <div data-testid="admin-programs">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-4xl">{t("programs.title")}</h1>
          <p className="text-eh-muted mt-1">{t("programs.subtitle")}</p>
        </div>
        {!editing && <button onClick={() => openEditor(null)} className="btn-primary text-sm" data-testid="program-new"><Plus size={14} /> {t("programs.new")}</button>}
      </div>

      {!editing && (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.length === 0 && <div className="col-span-full text-eh-muted text-sm bg-eh-cream border border-eh-border-soft rounded-2xl p-8 text-center">{t("programs.empty")}</div>}
          {items.map((p) => (
            <div key={p.id} className="bg-eh-cream border border-eh-border-soft rounded-2xl p-4 flex gap-4">
              <div className="w-20 h-20 rounded-xl bg-eh-bg overflow-hidden shrink-0 flex items-center justify-center">
                {p.image_url ? <img src={p.image_url} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={18} className="text-eh-muted" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-xs">
                  <span className={`px-2 py-0.5 rounded-full ${p.active ? "bg-eh-sage/30 text-eh-sage-deep" : "bg-eh-border-soft text-eh-muted"}`}>{p.active ? ta("programs.badge.active") : ta("programs.badge.inactive")}</span>
                  {p.featured && <span className="px-2 py-0.5 rounded-full bg-eh-ochre/30 text-eh-ochre-deep inline-flex items-center gap-1"><Sparkles size={10} /> {ta("programs.badge.featured")}</span>}
                  {isLegacyCurrency(p.currency, tenantCurrency) && (
                    <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-700" data-testid={`program-legacy-currency-${p.id}`}>
                      {ta("catalogue.currency.notPayable", { held: p.currency.toUpperCase() })}
                    </span>
                  )}
                  <span className="text-eh-muted">{p.duration_label}</span>
                </div>
                <div className="font-display text-lg mt-1 truncate">{loc(p, "name")}</div>
                <div className="text-sm text-eh-muted">{fmtMoney(p.price_cents, p.currency)}</div>
              </div>
              <div className="flex flex-col gap-1">
                <button onClick={() => openEditor(p)} className="p-2 rounded-full hover:bg-white" data-testid={`program-edit-${p.id}`}><Edit3 size={14} /></button>
                <button onClick={() => remove(p)} className="p-2 rounded-full text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="mt-6 bg-eh-cream border border-eh-border-soft rounded-3xl p-7" data-testid="program-editor">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display text-2xl">{editing === "new" ? t("programs.new") : tc("actions.edit")}</h2>
            <div className="flex gap-2">
              <button onClick={() => setEditing(null)} className="btn-ghost text-sm"><X size={14} /> {tc("actions.cancel")}</button>
              <button onClick={save} disabled={busy} className="btn-primary text-sm" data-testid="program-save">{busy ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} {tc("actions.save")}</button>
            </div>
          </div>
          <div className="space-y-4 mb-4">
            <LanguageAwareField
              label={ta("programs.editor.nameFr") ? ta("programs.editor.nameFr").replace(" (FR)", "").replace(" (EN)", "") : "Program Name"}
              valueFr={form.name_fr}
              valueEn={form.name_en}
              onChangeFr={(v) => updateName("fr", v)}
              onChangeEn={(v) => updateName("en", v)}
              requiredLang="any"
              error={nameError ? tc(nameError) : ""}
              testid="program-name"
            />
            <LanguageAwareField
              label={ta("programs.editor.taglineFr") ? ta("programs.editor.taglineFr").replace(" (FR)", "").replace(" (EN)", "") : "Tagline"}
              valueFr={form.tagline_fr}
              valueEn={form.tagline_en}
              onChangeFr={(v) => setForm({ ...form, tagline_fr: v })}
              onChangeEn={(v) => setForm({ ...form, tagline_en: v })}
              testid="program-tagline"
            />
            <LanguageAwareField
              label={ta("programs.editor.descriptionFr") ? ta("programs.editor.descriptionFr").replace(" (FR)", "").replace(" (EN)", "") : "Description"}
              valueFr={form.description_fr}
              valueEn={form.description_en}
              onChangeFr={(v) => setForm({ ...form, description_fr: v })}
              onChangeEn={(v) => setForm({ ...form, description_en: v })}
              multiline
              rows={3}
              testid="program-description"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Inp label={ta("programs.editor.duration")} value={form.duration_label} onChange={(v) => setForm({ ...form, duration_label: v })} />
            <Inp
              label={ta("programs.editor.price")}
              type="number"
              step="0.01"
              value={form._price_dollars}
              onChange={(v) => updateField("_price_dollars", v)}
              testid="program-price"
              error={fieldErrors._price_dollars ? ta(fieldErrors._price_dollars, { max: priceCeiling }) : ""}
            />
            {/* STATED, not chosen; switchable only for a row held in a retired currency.
                See CatalogueCurrencyField. */}
            <CatalogueCurrencyField
              currency={form.currency}
              denomination={tenantCurrency}
              onSwitch={(cur) => setForm((f) => ({ ...f, currency: cur }))}
              label={ta("programs.editor.currency")}
              testid="program"
            />
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-eh-muted">{ta("programs.editor.image")}</label>
              <div className="mt-1 flex items-center gap-3">
                {form.image_url ? <img src={form.image_url} alt="" className="w-14 h-14 rounded-lg object-cover" /> : <div className="w-14 h-14 rounded-lg bg-white border border-eh-border-soft flex items-center justify-center"><ImageIcon size={14} className="text-eh-muted" /></div>}
                <label className="btn-ghost text-xs cursor-pointer">{ta("programs.editor.upload")}<input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])} /></label>
              </div>
            </div>
            <Inp label={ta("programs.editor.order")} type="number" value={form.order} onChange={(v) => setForm({ ...form, order: v })} />
            <TaxCategoryField
              value={{ tax_category: form.tax_category, exemption_citation: form.exemption_citation, sac_code: form.sac_code }}
              onChange={(next) => setForm({ ...form, ...next })}
              country={taxProfile?.country}
            />
            <FeatList lang="fr" label={ta("programs.editor.featuresFr")} items={form.features_fr} input={featInput.fr} setInput={(v) => setFeatInput({ ...featInput, fr: v })} onAdd={() => addFeature("fr")} onRemove={(i) => removeFeature("fr", i)} addLabel={ta("programs.editor.addItem")} />
            <FeatList lang="en" label={ta("programs.editor.featuresEn")} items={form.features_en} input={featInput.en} setInput={(v) => setFeatInput({ ...featInput, en: v })} onAdd={() => addFeature("en")} onRemove={(i) => removeFeature("en", i)} addLabel={ta("programs.editor.addItem")} />
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 accent-eh-sage-deep" /> {ta("programs.badge.active")}</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="w-4 h-4 accent-eh-ochre-deep" /> {ta("programs.editor.featured")}</label>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * `error` is the message ALREADY RESOLVED by the caller's `t` (state holds an i18n key —
 * see lib/validation.js): it drives the border and the message under the input.
 */
const Inp = ({ label, value, onChange, type = "text", multiline, step, testid, error }) => (
  <div className={multiline ? "md:col-span-2" : ""}>
    <label className="text-xs uppercase tracking-[0.18em] text-eh-muted">{label}</label>
    {multiline ? <textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)} aria-invalid={error ? true : undefined} className={cn("mt-1 w-full bg-white border border-eh-border-soft rounded-xl px-3 py-2.5 text-sm resize-none", error && invalidInputClass)} data-testid={testid} /> : <input type={type} step={step} value={value} onChange={(e) => onChange(e.target.value)} aria-invalid={error ? true : undefined} className={cn("mt-1 w-full bg-white border border-eh-border-soft rounded-xl px-3 py-2.5 text-sm", error && invalidInputClass)} data-testid={testid} />}
    <FieldError message={error} testId={testid ? `${testid}-error` : undefined} />
  </div>
);

/**
 * `lang` is not decoration: it is the testid discriminator. The two lists are otherwise
 * identical, and the "Includes" draft box is the one editor input whose value lives
 * OUTSIDE `form` — the reset path has to be observable per language to be provable.
 */
const FeatList = ({ lang, label, items, input, setInput, onAdd, onRemove, addLabel }) => (
  <div>
    <label className="text-xs uppercase tracking-[0.18em] text-eh-muted">{label}</label>
    <div className="mt-1 flex gap-2">
      <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), onAdd())} placeholder={addLabel} className="flex-1 bg-white border border-eh-border-soft rounded-xl px-3 py-2 text-sm" data-testid={`program-features-${lang}-input`} />
      <button onClick={onAdd} className="btn-ghost text-xs" data-testid={`program-features-${lang}-add`}><Plus size={12} /></button>
    </div>
    <ul className="mt-2 space-y-1" data-testid={`program-features-${lang}-list`}>
      {items.map((f, i) => (
        <li key={i} className="flex items-center justify-between bg-white border border-eh-border-soft rounded-lg px-3 py-1.5 text-sm">
          <span>{f}</span>
          <button onClick={() => onRemove(i)} className="text-red-600 hover:bg-red-50 p-1 rounded"><Trash2 size={11} /></button>
        </li>
      ))}
    </ul>
  </div>
);
